import React, { useCallback, useContext } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import htmlParser from 'html-react-parser';
import sanitizeHtml from 'sanitize-html-react';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import CommonConstants from 'sg-common-constants';
import { HighlightPhraseFilter } from 'gw-portals-util-js';
import { AccordionCard } from '@jutro/components';

import styles from './SgFaqItemComponent.module.scss';

function SgFaqItemComponent(props) {
    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';
    const { value, searchText, messages } = props;

    const readValue = useCallback(
        (id, compPath) => {
            const currentSection = _.get(value, compPath);
            return translator(_.find(messages, { id: currentSection }));
        },
        [messages, translator, value]
    );

    const highlightText = useCallback((textToHighlight) => {
        const highlightedText = HighlightPhraseFilter.highlightText(
            textToHighlight,
            searchText,
            styles.gwHighlightedText
        );
        return htmlParser(highlightedText);
    }, [searchText]);

    const renderAccordionHeader = useCallback(
        (isOpen) => {
            const id = readValue('accordionId', 'id');
            const plainTitleText = readValue('accordionHeader', 'question');

            return (
                <div id={`accordionHeader${id}`} style={{ flex: 'auto' }}>
                    <h4>{highlightText(plainTitleText)}</h4>
                    {isOpen && (
                        <span id="chevronOpened">
                            <i className="mir mi-expand-less" />
                        </span>
                    )
                    }
                    {!isOpen && (
                        <span id="chevronClosed">
                            <i className="mir mi-expand-more" />
                        </span>
                    )}
                </div>
            );
        },
        [highlightText, messages, readValue, translator]
    );

    const renderTextComponent = useCallback(() => {
        const hasOrderedList = _.get(value, 'hasOrderedList');
        const answers = _.get(value, 'answers');
        const nonListItemAnswers = hasOrderedList ? answers.filter((ans) => !ans.isListItem) : answers;
        const parsedAnswers = _.map(nonListItemAnswers, (ans) => {
            if (ans.isTable) {
                const tableRowsNumbers = Array.from(
                    { length: Object.keys(ans).filter((key) => key.startsWith('col1')).length },
                    (element, index) => index
                );
                return (
                    <div>
                        <table className="sgFaqTable">
                            <thead>
                                <tr>
                                    <th>{translator(_.find(messages, { id: ans.header1 }))}</th>
                                    <th>{translator(_.find(messages, { id: ans.header2 }))}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tableRowsNumbers.map((rowNumber) => {
                                    return (
                                        <tr key={rowNumber}>
                                            <td>{translator(_.find(messages, { id: ans[`col1row${rowNumber}`] }))}</td>
                                            <td>{translator(_.find(messages, { id: ans[`col2row${rowNumber}`] }))}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                );
            }

            const allowedTags = ['a', 'b', 'br'];
            let sanitizedAnswer;
            if (ans.phoneNumber) {
                sanitizedAnswer = ans.phoneNumber2
                    ? sanitizeHtml(translator(_.find(messages, { id: ans.message }), {
                        phoneNumber: isMobile
                            ? (
                                // eslint-disable-next-line max-len
                                `&lt;a href="tel:${CommonConstants.SAGA_PHONE_NUMBERS[ans.phoneNumber].replaceAll(' ', '')}"&gt;${CommonConstants.SAGA_PHONE_NUMBERS[ans.phoneNumber]}&lt;/a&gt;`
                            ) : CommonConstants.SAGA_PHONE_NUMBERS[ans.phoneNumber],
                        phoneNumber2: isMobile
                            ? (
                                // eslint-disable-next-line max-len
                                `&lt;a href="tel:${CommonConstants.SAGA_PHONE_NUMBERS[ans.phoneNumber2].replaceAll(' ', '')}"&gt;${CommonConstants.SAGA_PHONE_NUMBERS[ans.phoneNumber2]}&lt;/a&gt;`
                            ) : CommonConstants.SAGA_PHONE_NUMBERS[ans.phoneNumber2]
                    }), { allowedTags })
                    : sanitizeHtml(translator(_.find(messages, { id: ans.message }), {
                        phoneNumber: isMobile
                            ? (
                                // eslint-disable-next-line max-len
                                `&lt;a href="tel:${CommonConstants.SAGA_PHONE_NUMBERS[ans.phoneNumber].replaceAll(' ', '')}"&gt;${CommonConstants.SAGA_PHONE_NUMBERS[ans.phoneNumber]}&lt;/a&gt;`
                            ) : CommonConstants.SAGA_PHONE_NUMBERS[ans.phoneNumber]
                    }), { allowedTags });
            } else {
                sanitizedAnswer = sanitizeHtml(translator(_.find(messages, { id: ans.message })), { allowedTags });
            }

            if (ans.isHeading) {
                return (
                    <h3 key={ans.message} className={`${styles.gwFaqText}`}>
                        {highlightText(sanitizedAnswer)}
                    </h3>
                );
            }

            return (
                <p key={ans.message} className={`${styles.gwFaqText} ${ans.hasArrow ? 'sgFaqTextBullet' : ''}`}>
                    {highlightText(sanitizedAnswer)}
                </p>
            );
        });
        if (hasOrderedList) {
            const listItemAnswers = answers.filter((ans) => ans.isListItem);
            const orderedList = (
                <ol key={value.id}>
                    {
                        listItemAnswers.map((ans) => {
                            return (
                                <li key={ans.message}>{highlightText(translator(_.find(messages, { id: ans.message })))}</li>);
                        })
                    }
                </ol>
            );
            const indexToInsertList = answers.findIndex((ans) => ans.isListItem);
            parsedAnswers.splice(indexToInsertList, 0, orderedList);
        }
        return parsedAnswers;
    }, [highlightText, messages, translator, value, isMobile]);

    return (
        <div className={styles.gwFaqItemContainer}>
            <AccordionCard
                id={`accordion_${_.get(value, 'id')}`}
                renderHeader={renderAccordionHeader}
                cardTitleClassName="titleContainer"
            >
                {renderTextComponent()}
            </AccordionCard>
        </div>
    );
}

SgFaqItemComponent.propTypes = {
    value: PropTypes.shape({}).isRequired,
    searchText: PropTypes.string.isRequired,
    messages: PropTypes.shape({}).isRequired
};
export default SgFaqItemComponent;
